import { createContext, useContext, useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/api';
import { useAuth } from './AuthContext';
import { uploadFileWithProgress, getUploadContentType } from '../utils/gcsUpload';

const UploadContext = createContext({});

// How often we sweep uploading files for stalls.
const STALL_CHECK_INTERVAL_MS = 5000;
// No progress event for this long while uploading => flip to `stalled` (UI only, request keeps running).
const STALL_THRESHOLD_MS = 20000;
// Hard cap per file — abort and mark `failed` if a single PUT runs this long.
const UPLOAD_CAP_MS = 10 * 60 * 1000;

let idSeq = 0;
const makeFileId = () =>
    (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function')
        ? crypto.randomUUID()
        : `file-${Date.now()}-${idSeq++}`;

const toPublicFile = (id, file) => ({
    id,
    name: file.name,
    size: file.size,
    percent: 0,
    status: 'pending', // pending | uploading | stalled | failed | done | removed
    error: null,
});

export const UploadProvider = ({ children }) => {
    const { user } = useAuth();

    const [caseId, setCaseId] = useState(null);
    const [facilityType, setFacilityType] = useState(null);
    const [processTriggered, setProcessTriggered] = useState(false);
    const [processError, setProcessError] = useState(null);
    const [files, setFiles] = useState([]);
    // Reactive mirror of autoProcessRef (below) — exposed publicly so
    // consumers (EditCase) can tell whether the *current* batch will
    // auto-fire process-case once uploads finish, and keep showing upload
    // progress through that window instead of racing the button that
    // fires process-case manually. See autoProcessRef for why the ref
    // exists too.
    const [autoProcess, setAutoProcessState] = useState(true);

    // Mirrors `files`/`user` for use inside async callbacks that must always
    // see the latest value without becoming stale closures.
    const filesRef = useRef(files);
    const userRef = useRef(user);
    useEffect(() => { userRef.current = user; }, [user]);
    // Captured when a batch starts so the finalize idempotency key stays
    // stable even if auth state churns mid-upload.
    const batchUidRef = useRef(null);

    // Per-file bookkeeping that doesn't belong in React state: the raw File,
    // its in-flight AbortController, timers, and last-progress timestamp.
    const fileMetaRef = useRef(new Map());

    // Identifies the current upload batch. Bumped on every cancelAll/
    // startCaseUpload so stale async work (from a superseded batch) can
    // recognize itself as stale and become a no-op instead of corrupting
    // state for the new batch.
    const batchIdRef = useRef(0);

    const caseIdRef = useRef(null);
    const facilityTypeRef = useRef(null);
    // Guards the single process-case call. A ref (not derived state) so
    // StrictMode's double-invoke of effects/callbacks can't fire it twice.
    const processTriggeredRef = useRef(false);
    // Whether the *current* batch should auto-fire process-case once every
    // file is done. true for startCaseUpload (new-case flow); addFilesToCase
    // callers can opt out (e.g. EditCase's "add more files to an existing
    // case" flow, where the user always clicks Extract/Re-extract manually).
    // A ref (for maybeTriggerProcessCase's synchronous check) kept in sync
    // with the `autoProcess` state above (for rendering).
    const autoProcessRef = useRef(true);
    const setAutoProcess = useCallback((value) => {
        autoProcessRef.current = value;
        setAutoProcessState(value);
    }, []);

    // Applies `updater` against the latest known files array and commits the
    // result to both the ref (synchronously, for immediately-following logic
    // like maybeTriggerProcessCase) and React state (for rendering). We
    // deliberately don't rely on setFiles' functional-updater form to read
    // "prev" — React may defer invoking it, which would make filesRef stale
    // for any code that runs right after calling updateFiles.
    const updateFiles = useCallback((updater) => {
        const next = updater(filesRef.current);
        filesRef.current = next;
        setFiles(next);
        return next;
    }, []);

    const abortAllMeta = useCallback(() => {
        fileMetaRef.current.forEach((meta) => {
            if (meta.controller) meta.controller.abort();
            if (meta.capTimeoutId) clearTimeout(meta.capTimeoutId);
        });
        fileMetaRef.current.clear();
    }, []);

    // Cancels any in-flight batch: aborts requests/timers and invalidates
    // the batch id so in-flight promises recognize themselves as stale.
    const cancelAll = useCallback(() => {
        batchIdRef.current += 1;
        abortAllMeta();
    }, [abortAllMeta]);

    // Fires /case/process-case exactly once, when every non-removed file in
    // the current batch has reached `done`.
    const maybeTriggerProcessCase = useCallback((batchId) => {
        if (batchId !== batchIdRef.current) return;
        if (processTriggeredRef.current) return;
        if (!autoProcessRef.current) return;

        const relevant = filesRef.current.filter((f) => f.status !== 'removed');
        if (relevant.length === 0) return;
        if (!relevant.every((f) => f.status === 'done')) return;

        processTriggeredRef.current = true;
        setProcessTriggered(true);

        api.post('/case/process-case', {
            case_id: caseIdRef.current,
            facility_type: facilityTypeRef.current,
        }, {
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            withCredentials: true,
        }).catch((err) => {
            console.error('Failed to trigger processing:', err);
            setProcessError(err);
        });
    }, []);

    // Uploads a single file: fresh signed URL -> PUT (with progress) ->
    // finalize-upload. One automatic retry on network error; after that the
    // file is marked `failed` and the caller moves on to the next file.
    const uploadOneFile = useCallback(async (id, batchId, { attempt = 0, onCaseIdKnown } = {}) => {
        if (batchId !== batchIdRef.current) return;
        const meta = fileMetaRef.current.get(id);
        if (!meta) return;
        const { file } = meta;

        updateFiles((prev) => prev.map((f) => (
            f.id === id ? { ...f, status: 'uploading', error: null } : f
        )));

        const controller = new AbortController();
        meta.controller = controller;
        meta.lastProgressAt = Date.now();
        // Reset per-attempt; only the cap timeout below sets this back to
        // true, so we can tell a cap-triggered abort apart from a genuine
        // transient network error in the catch block.
        meta.capped = false;
        meta.capTimeoutId = setTimeout(() => {
            meta.capped = true;
            controller.abort();
        }, UPLOAD_CAP_MS);

        try {
            const hadCaseId = !!caseIdRef.current;
            const contentType = getUploadContentType(file);

            const initForm = new FormData();
            initForm.append('file_name', file.name);
            initForm.append('content_type', contentType);
            if (caseIdRef.current) {
                initForm.append('case_id', caseIdRef.current);
            }
            const initRes = await api.post('/case/upload-url', initForm, { withCredentials: true });
            const { case_id, signed_url, gcs_uri } = initRes.data;
            if (!case_id || !signed_url || !gcs_uri) {
                throw new Error('Failed to obtain signed upload URL');
            }

            if (!hadCaseId) {
                caseIdRef.current = case_id;
                setCaseId(case_id);
                if (onCaseIdKnown) onCaseIdKnown(case_id);
            }
            if (batchId !== batchIdRef.current) return;

            await uploadFileWithProgress(signed_url, file, contentType, {
                signal: controller.signal,
                onProgress: ({ percent }) => {
                    meta.lastProgressAt = Date.now();
                    updateFiles((prev) => prev.map((f) => (
                        f.id === id ? { ...f, percent, status: 'uploading' } : f
                    )));
                },
            });
            if (batchId !== batchIdRef.current) return;

            // Idempotency key matches NewCase.js's existing scheme so retries
            // (manual or automatic) don't create duplicate file entries.
            const idempotencyKey = `${batchUidRef.current ?? userRef.current?.uid}-${case_id}-${file.name}-${file.size}-${file.lastModified}`;
            const finalizeForm = new FormData();
            finalizeForm.append('case_id', case_id);
            finalizeForm.append('file_name', file.name);
            finalizeForm.append('gcs_uri', gcs_uri);
            finalizeForm.append('idempotency_key', idempotencyKey);
            finalizeForm.append('facility_type', facilityTypeRef.current);
            await api.post('/case/finalize-upload', finalizeForm, { withCredentials: true });
            if (batchId !== batchIdRef.current) return;

            clearTimeout(meta.capTimeoutId);
            updateFiles((prev) => prev.map((f) => (
                f.id === id ? { ...f, status: 'done', percent: 100, error: null } : f
            )));
            maybeTriggerProcessCase(batchId);
        } catch (err) {
            clearTimeout(meta.capTimeoutId);
            if (batchId !== batchIdRef.current) return; // batch was cancelled — drop silently

            // Network errors (no response, e.g. offline) get one automatic
            // retry with a fresh signed URL; anything else (or a second
            // failure) is a terminal failure for this file. A cap-triggered
            // abort is deliberately excluded from the retry path — the hard
            // 10-min cap must fail the file outright, not buy it a second
            // ~10-min attempt.
            const isNetworkError = !err.response;
            if (!meta.capped && isNetworkError && attempt < 1) {
                await uploadOneFile(id, batchId, { attempt: attempt + 1, onCaseIdKnown });
                return;
            }

            const message = meta.capped ? 'Upload timed out' : (err.message || 'Upload failed');
            updateFiles((prev) => prev.map((f) => (
                f.id === id ? { ...f, status: 'failed', error: message } : f
            )));
        }
    }, [updateFiles, maybeTriggerProcessCase]);

    const runBatch = useCallback(async (batchId, ids, { onCaseIdKnown } = {}) => {
        for (const id of ids) {
            if (batchId !== batchIdRef.current) return;
            await uploadOneFile(id, batchId, { onCaseIdKnown });
        }
    }, [uploadOneFile]);

    // Registers file entries + metadata for a set of newly-selected files
    // and returns their ids.
    const registerFiles = useCallback((filesToUpload) => {
        const entries = filesToUpload.map((file) => ({ id: makeFileId(), file }));
        entries.forEach(({ id, file }) => {
            fileMetaRef.current.set(id, { file, controller: null, capTimeoutId: null, lastProgressAt: Date.now() });
        });
        return entries;
    }, []);

    const startCaseUpload = useCallback((filesToUpload, { facilityType: ft } = {}) => {
        // Single active case: a new upload supersedes any in-flight batch.
        cancelAll();
        const batchId = batchIdRef.current;
        batchUidRef.current = userRef.current?.uid ?? null;

        facilityTypeRef.current = ft;
        setFacilityType(ft);
        caseIdRef.current = null;
        setCaseId(null);
        setAutoProcess(true);
        processTriggeredRef.current = false;
        setProcessTriggered(false);
        setProcessError(null);

        const entries = registerFiles(filesToUpload);
        const publicFiles = entries.map(({ id, file }) => toPublicFile(id, file));
        filesRef.current = publicFiles;
        setFiles(publicFiles);

        const ids = entries.map((e) => e.id);

        return new Promise((resolve, reject) => {
            let settled = false;
            const onCaseIdKnown = (cid) => {
                if (!settled) {
                    settled = true;
                    resolve(cid);
                }
            };
            runBatch(batchId, ids, { onCaseIdKnown }).then(() => {
                if (!settled) {
                    settled = true;
                    reject(new Error('Failed to create case: all uploads failed'));
                }
            }).catch((err) => {
                if (!settled) {
                    settled = true;
                    reject(err);
                }
            });
        });
    }, [cancelAll, registerFiles, runBatch, setAutoProcess]);

    const addFilesToCase = useCallback((existingCaseId, filesToUpload, { autoProcess: shouldAutoProcess = true } = {}) => {
        // UploadContext runs a single, strictly-sequential batch at a time.
        // If the caller is adding files to a DIFFERENT case than whatever
        // batch is currently live, the old batch must be cancelled outright
        // — not silently repointed. Without this, the still-in-flight
        // files from the old case would fetch their upload-url and
        // finalize using the *new* caseIdRef, merging one patient's
        // documents into another patient's case (a PHI contamination bug),
        // and the old case's completion would never fire its own
        // process-case (the batch's `relevant` files array would end up a
        // mix of both cases', evaluated under the new case's id).
        const isDifferentCase = caseIdRef.current !== null && caseIdRef.current !== existingCaseId;
        if (isDifferentCase) {
            cancelAll();
            filesRef.current = [];
            setFiles([]);
        }

        const batchId = batchIdRef.current;
        batchUidRef.current = userRef.current?.uid ?? null;
        caseIdRef.current = existingCaseId;
        setCaseId(existingCaseId);
        setAutoProcess(shouldAutoProcess);
        // Reset the process-case guard for this (fresh-or-continuing) batch.
        // Same-case call: new files may arrive after a previous batch for
        // this case already finished and triggered process-case — allow it
        // to fire again once these finish too (unless this call opted out
        // of auto-processing entirely). Different-case call: the guard
        // must start false regardless of the old case's history, since
        // `maybeTriggerProcessCase` only checks the ref/batchId, not which
        // case it belongs to — a stale `true` here would silently prevent
        // the new case from ever auto-firing.
        processTriggeredRef.current = false;
        setProcessTriggered(false);

        const entries = registerFiles(filesToUpload);
        updateFiles((prev) => [...prev, ...entries.map(({ id, file }) => toPublicFile(id, file))]);

        const ids = entries.map((e) => e.id);
        runBatch(batchId, ids);
    }, [cancelAll, registerFiles, runBatch, updateFiles, setAutoProcess]);

    const retryFile = useCallback((id) => {
        const meta = fileMetaRef.current.get(id);
        if (!meta) return;
        // uploadOneFile always requests a fresh signed URL before the PUT,
        // which is what we need here since signed URLs expire in 15 min.
        uploadOneFile(id, batchIdRef.current, { attempt: 0 });
    }, [uploadOneFile]);

    const removeFile = useCallback((id) => {
        updateFiles((prev) => prev.map((f) => (
            (f.id === id && f.status === 'failed') ? { ...f, status: 'removed' } : f
        )));
        maybeTriggerProcessCase(batchIdRef.current);
    }, [updateFiles, maybeTriggerProcessCase]);

    const clear = useCallback(() => {
        cancelAll();
        caseIdRef.current = null;
        facilityTypeRef.current = null;
        processTriggeredRef.current = false;
        setCaseId(null);
        setFacilityType(null);
        setAutoProcess(true);
        setProcessTriggered(false);
        setProcessError(null);
        filesRef.current = [];
        setFiles([]);
    }, [cancelAll, setAutoProcess]);

    // Stall detection: sweep every 5s; a file with no progress event in the
    // last 20s flips to `stalled` (UI-only — the request keeps running and a
    // subsequent progress event flips it back to `uploading`).
    useEffect(() => {
        const intervalId = setInterval(() => {
            const now = Date.now();
            let changed = false;
            updateFiles((prev) => {
                const next = prev.map((f) => {
                    if (f.status !== 'uploading') return f;
                    const meta = fileMetaRef.current.get(f.id);
                    if (!meta) return f;
                    if (now - meta.lastProgressAt > STALL_THRESHOLD_MS) {
                        changed = true;
                        return { ...f, status: 'stalled' };
                    }
                    return f;
                });
                return changed ? next : prev;
            });
        }, STALL_CHECK_INTERVAL_MS);
        return () => clearInterval(intervalId);
    }, [updateFiles]);

    // Warn before leaving the page while any file is actively uploading.
    // Depends on a derived boolean (not the `files` array itself) so the
    // listener isn't torn down/re-registered on every progress-percent tick.
    // `pending` counts too: queued files are lost on refresh just as surely
    // as the one currently in flight.
    const anyUploading = files.some((f) => f.status === 'uploading' || f.status === 'stalled' || f.status === 'pending');
    useEffect(() => {
        if (!anyUploading) return undefined;

        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [anyUploading]);

    const value = {
        caseId,
        facilityType,
        processTriggered,
        processError,
        autoProcess,
        files,
        startCaseUpload,
        addFilesToCase,
        retryFile,
        removeFile,
        cancelAll,
        clear,
    };

    return (
        <UploadContext.Provider value={value}>
            {children}
        </UploadContext.Provider>
    );
};

export const useUpload = () => useContext(UploadContext);
